import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { MultiFilter } from 'components/MultiFilters';
import { queryToFilters } from 'lib/utils/filterUtils';
import { FilterResult } from 'lib/types/filters';
import { GeoLocationFilter } from 'components/Filters/GeoLocationFilter';
import { SortFilter } from 'components/Filters/SortFilter';
import { JobSearchCriteria } from 'lib/types/jobs';
import { useCurrentUser } from 'lib/contexts/ApplicationState';
import { userIsAdmin } from 'lib/helpers';
import { jobSortOptions, queryToCriteria } from './utils';
import { JobTypeFilter } from './JobTypeFilter';
import { LocationTypeFilter } from './LocationTypeFilter';
import { PayFilter } from './PayFilter';
import { StatusFilter } from './StatusFilter';
import { VisibilityFilter } from './VisibilityFilter';

interface Props {
  onChange: (criteria: JobSearchCriteria) => void;
}

export const FilterCriteria = ({ onChange }: Props) => {
  const router = useRouter();
  const currentUser = useCurrentUser();

  const [criteria, setCriteria] = useState<JobSearchCriteria | undefined>();
  const [filters, setFilters] = useState<(FilterResult|string)[]>([]);

  useEffect(() => {
    if (criteria) onChange(criteria);
  }, [criteria]);

  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    setFilters(queryToFilters(router.query));
    setCriteria(queryToCriteria(router.query));
  }, [router]);

  if (!criteria) return null;
  const canViewTeamFeatures = userIsAdmin(currentUser)
    || (currentUser?.hasPaidTeam && !!currentUser?.managedAccounts?.length);

  return (
    <div className="max-w-screen-2xl m-auto">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
        <div className="col-span-2 md:col-span-4">
          <MultiFilter
            filters={filters}
            placeholder="Search companies, roles, keywords"
            apiPath="/filters/jobs"
            useElasticSearch={false}
          />
        </div>
        <div className="col-span-1">
          <JobTypeFilter selected={criteria.jobTypes} />
        </div>
        <div className="col-span-1">
          <LocationTypeFilter selected={criteria.locationType} />
        </div>
        <div className="col-span-2">
          <GeoLocationFilter />
        </div>
        <div className="col-span-1">
          <PayFilter payMin={criteria.payMin} payType={criteria.payType} />
        </div>
        {canViewTeamFeatures && (
          <div className="col-span-1 flex space-x-2">
            <StatusFilter selected={criteria.status} />
            <VisibilityFilter selected={criteria.visibility} />
          </div>
        )}
        <div className="col-span-2 md:col-span-5">
          <div className="flex justify-end">
            <SortFilter sortOptions={jobSortOptions} defaultSort="createdAt" sort={criteria.sort} />
          </div>
        </div>
      </div>
    </div>
  );
};
